import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Button,
  Modal,
  Checkbox,
  message,
  Space,
  Tooltip,
  Typography,
  Collapse,
  Card,
  Progress,
  Tag,
  Divider,
  Alert,
  Row,
  Col,
  Badge,
  Statistic,
  Tabs
} from 'antd';
import API from '../API/API';
import './LeadDownload.css';
import {
  DownloadOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  FileExcelOutlined,
  CarOutlined,
  UserOutlined,
  PhoneOutlined,
  SettingOutlined,
  DollarOutlined,
  HistoryOutlined,
  GlobalOutlined,
  EnvironmentOutlined,
  ExportOutlined,
  FilterOutlined,
  ColumnHeightOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ArrowLeftOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  CloudDownloadOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import GoogleAdsSheetDownload from './GoogleAdsSheetDownload';
import { extractGclid } from './googleAdsSheetUtils';

const { Text, Title } = Typography;
const { Panel } = Collapse;
const { RangePicker } = DatePicker;

const getCurrentYardEntry = (yards = []) => {
  const items = Array.isArray(yards) ? yards.filter(Boolean) : [];
  if (items.length === 0) {
    return null;
  }

  for (let index = items.length - 1; index >= 0; index -= 1) {
    if (items[index]?.isCurrent === true) {
      return items[index];
    }
  }

  return items[items.length - 1];
};

const LeadDownload = ({ filters, columns, dateRange, buttonProps }) => {
  // Initialize with today's date range if no dateRange prop is provided
  // But if dateRange prop is provided, use it as default

  // Add these state variables at the top of your component
  const [activeTab, setActiveTab] = useState('personalInfo');

  const [localDateRange, setLocalDateRange] = useState(
    dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]
      ? dateRange
      : [dayjs().startOf('day'), dayjs().endOf('day')]
  );

  // Organized field groups based on your JSON structure
  const fieldGroups = [
    {
      title: 'Personal Information',
      icon: <UserOutlined style={{ color: '#1890ff' }} />,
      key: 'personalInfo',
      color: '#1890ff',
      fields: [
        { label: 'Full Name', value: 'personalInfo.fullName', path: 'personalInfo.fullName' },
        { label: 'Email', value: 'personalInfo.email', path: 'personalInfo.email' },
        { label: 'Phone Number', value: 'personalInfo.phoneNumber', path: 'personalInfo.phoneNumber' },
        { label: 'Phone Number Format', value: 'phoneValidation.reason', path: 'phoneValidation.reason' },
        { label: 'Zip Code', value: 'personalInfo.zipCode', path: 'personalInfo.zipCode' },
        { label: 'Customer Time Zone', value: 'personalInfo.customerTimeZone', path: 'personalInfo.customerTimeZone' }
      ]
    },
    {
      title: 'Vehicle Information',
      icon: <CarOutlined style={{ color: '#52c41a' }} />,
      key: 'vehicleInfo',
      color: '#52c41a',
      fields: [
        { label: 'Year', value: 'vehicleInfo.year', path: 'vehicleInfo.year' },
        { label: 'Make', value: 'vehicleInfo.make', path: 'vehicleInfo.make' },
        { label: 'Model', value: 'vehicleInfo.model', path: 'vehicleInfo.model' },
        { label: 'VIN', value: 'vehicleInfo.vin', path: 'vehicleInfo.vin' },
        { label: 'Part', value: 'vehicleInfo.part', path: 'vehicleInfo.part' }
      ]
    },
    {
      title: 'Lead  Details',
      icon: <SettingOutlined style={{ color: '#722ed1' }} />,
      key: 'leadStatus',
      color: '#722ed1',
      fields: [
        { label: 'Lead Stage', value: 'leadStage', path: 'leadStage' },
        { label: 'Lead Type', value: 'leadType', path: 'leadType' },
        { label: 'Tier Type', value: 'tierType', path: 'tierType' },
        { label: 'Domain', value: 'domain', path: 'domain' },
        { label: 'Campaign Name', value: 'campaignName', path: 'campaignName' },
        { label: 'Landing URL', value: 'landingUrl', path: 'landingUrl' },
        { label: 'Google Click ID', value: 'gclid', path: 'gclid' },
        { label: 'Status', value: 'status', path: 'status' },
        { label: 'Secondary Status', value: 'secondaryStatus', path: 'secondaryStatus' },
        { label: 'Score', value: 'score', path: 'score' },
        { label: 'Is Existing Customer', value: 'isExistingCustomer', path: 'isExistingCustomer' },
        { label: 'Queue Lead', value: 'queueLead', path: 'queueLead' },
        { label: 'Missed Follow Up', value: 'missedFollowUp', path: 'missedFollowUp' },
        { label: 'Follow Up Date', value: 'followUpDate', path: 'followUpDate' },
        { label: 'Warranty', value: 'warranty', path: 'warranty' },
        { label: 'created At', value: 'createdAt', path: 'createdAt' },
        { label: 'updated At', value: 'updatedAt', path: 'updatedAt' },

      ]
    },
    {
      title: 'Agent & Assignment',
      icon: <UserOutlined style={{ color: '#fa8c16' }} />,
      key: 'agentInfo',
      color: '#fa8c16',
      fields: [
        { label: 'Agent Name', value: 'agentName', path: 'agentName' },
        { label: 'Assigned Date', value: 'assignedDate', path: 'assignedDate' }
      ]
    },
    {
      title: 'Location Information',
      icon: <EnvironmentOutlined style={{ color: '#13c2c2' }} />,
      key: 'locationInfo',
      color: '#13c2c2',
      fields: [
        // IP & Basic
        { label: 'IP Address', value: 'locationInfo.ip', path: 'locationInfo.ip' },
        { label: 'Lookup Success', value: 'locationInfo.success', path: 'locationInfo.success' },
        { label: 'IP Type', value: 'locationInfo.type', path: 'locationInfo.type' },

        // Continent
        { label: 'Continent', value: 'locationInfo.continent', path: 'locationInfo.continent' },
        { label: 'Continent Code', value: 'locationInfo.continent_code', path: 'locationInfo.continent_code' },

        // Country
        { label: 'Country', value: 'locationInfo.country', path: 'locationInfo.country' },
        { label: 'Country Code', value: 'locationInfo.country_code', path: 'locationInfo.country_code' },

        // Region / City
        { label: 'Region', value: 'locationInfo.region', path: 'locationInfo.region' },
        { label: 'Region Code', value: 'locationInfo.region_code', path: 'locationInfo.region_code' },
        { label: 'City', value: 'locationInfo.city', path: 'locationInfo.city' },

        // Geo Coordinates
        { label: 'Latitude', value: 'locationInfo.latitude', path: 'locationInfo.latitude' },
        { label: 'Longitude', value: 'locationInfo.longitude', path: 'locationInfo.longitude' },

        // Country Info
        { label: 'Postal Code', value: 'locationInfo.postal', path: 'locationInfo.postal' },
        { label: 'Calling Code', value: 'locationInfo.calling_code', path: 'locationInfo.calling_code' },
        { label: 'Capital', value: 'locationInfo.capital', path: 'locationInfo.capital' },
        { label: 'Borders', value: 'locationInfo.borders', path: 'locationInfo.borders' },

        // EU Flag
        { label: 'Is EU', value: 'locationInfo.is_eu', path: 'locationInfo.is_eu' },

        // Flag Data
        { label: 'Flag Emoji', value: 'locationInfo.flag.emoji', path: 'locationInfo.flag.emoji' },
        { label: 'Flag Unicode', value: 'locationInfo.flag.emoji_unicode', path: 'locationInfo.flag.emoji_unicode' },
        { label: 'Flag Image', value: 'locationInfo.flag.img', path: 'locationInfo.flag.img' },

        // Connection / ISP
        { label: 'ASN', value: 'locationInfo.asn', path: 'locationInfo.asn' },
        { label: 'Organization', value: 'locationInfo.org', path: 'locationInfo.org' },
        { label: 'Network', value: 'locationInfo.network', path: 'locationInfo.network' },

        { label: 'ISP Name', value: 'locationInfo.connection.isp', path: 'locationInfo.connection.isp' },
        { label: 'ISP Domain', value: 'locationInfo.connection.domain', path: 'locationInfo.connection.domain' },
        { label: 'Connection ASN', value: 'locationInfo.connection.asn', path: 'locationInfo.connection.asn' },
        { label: 'Connection Org', value: 'locationInfo.connection.org', path: 'locationInfo.connection.org' },

        // Timezone
        { label: 'Timezone ID', value: 'locationInfo.timezone.id', path: 'locationInfo.timezone.id' },
        { label: 'Timezone Abbreviation', value: 'locationInfo.timezone.abbr', path: 'locationInfo.timezone.abbr' },
        { label: 'DST Active', value: 'locationInfo.timezone.is_dst', path: 'locationInfo.timezone.is_dst' },
        { label: 'UTC Offset', value: 'locationInfo.timezone.offset', path: 'locationInfo.timezone.offset' },
        { label: 'UTC String', value: 'locationInfo.timezone.utc', path: 'locationInfo.timezone.utc' },
        { label: 'Current Time', value: 'locationInfo.timezone.current_time', path: 'locationInfo.timezone.current_time' },

        // Currency
        { label: 'Currency Name', value: 'locationInfo.currency.name', path: 'locationInfo.currency.name' },
        { label: 'Currency Code', value: 'locationInfo.currency.code', path: 'locationInfo.currency.code' },
        { label: 'Currency Symbol', value: 'locationInfo.currency.symbol', path: 'locationInfo.currency.symbol' },
        { label: 'Currency Plural', value: 'locationInfo.currency.plural', path: 'locationInfo.currency.plural' },
        { label: 'Exchange Rate', value: 'locationInfo.currency.exchange_rate', path: 'locationInfo.currency.exchange_rate' },

        // Security
        { label: 'Anonymous', value: 'locationInfo.security.anonymous', path: 'locationInfo.security.anonymous' },
        { label: 'Proxy', value: 'locationInfo.security.proxy', path: 'locationInfo.security.proxy' },
        { label: 'VPN', value: 'locationInfo.security.vpn', path: 'locationInfo.security.vpn' },
        { label: 'TOR', value: 'locationInfo.security.tor', path: 'locationInfo.security.tor' },
        { label: 'Hosting', value: 'locationInfo.security.hosting', path: 'locationInfo.security.hosting' },

        // Rate Limit Info
        { label: 'Rate Limit', value: 'locationInfo.rate.limit', path: 'locationInfo.rate.limit' },
        { label: 'Remaining Requests', value: 'locationInfo.rate.remaining', path: 'locationInfo.rate.remaining' }
      ]
    },
    {
      title: 'Browser Information',
      icon: <GlobalOutlined style={{ color: '#eb2f96' }} />,
      key: 'browserInfo',
      color: '#eb2f96',
      fields: [
        { label: 'User Agent', value: 'browserInfo.userAgent', path: 'browserInfo.userAgent' },
        { label: 'Platform', value: 'browserInfo.platform', path: 'browserInfo.platform' },
        { label: 'Language', value: 'browserInfo.language', path: 'browserInfo.language' },
        { label: 'Languages', value: 'browserInfo.languages', path: 'browserInfo.languages' },
        { label: 'Cookie Enabled', value: 'browserInfo.cookieEnabled', path: 'browserInfo.cookieEnabled' },
        { label: 'Java Enabled', value: 'browserInfo.javaEnabled', path: 'browserInfo.javaEnabled' },
        { label: 'PDF Viewer Enabled', value: 'browserInfo.pdfViewerEnabled', path: 'browserInfo.pdfViewerEnabled' }
      ]
    },
    {
      title: 'Payment & Financial',
      icon: <DollarOutlined style={{ color: '#f5222d' }} />,
      key: 'paymentInfo',
      color: '#f5222d',
      fields: [
        { label: 'Payment Status', value: 'paymentStatus', path: 'paymentStatus' },
        { label: 'Total Amount', value: 'totalAmount', path: 'totalAmount' },
        { label: 'Paid Amount', value: 'paidAmount', path: 'paidAmount' },
        { label: 'Balance Amount', value: 'balanceAmount', path: 'balanceAmount' },
        { label: 'Quoted Value', value: 'quotedValue[0].price', path: 'quotedValue[0].price' },
        { label: 'Quoted Value Date', value: 'quotedValue[0].addedAt', path: 'quotedValue[0].addedAt' },
        { label: 'Zoho Status', value: 'zohoStatus', path: 'zohoStatus' }
      ]
    },
    {
      title: 'Advance Payments',
      icon: <SafetyCertificateOutlined style={{ color: '#fa541c' }} />,
      key: 'advancePaymentsInfo',
      color: '#fa541c',
      fields: [
        { label: 'Payment ID', value: 'advancePayments[0].id', path: 'advancePayments[0].id' },
        { label: 'Payment Amount', value: 'advancePayments[0].amount', path: 'advancePayments[0].amount' },
        { label: 'Payment Method', value: 'advancePayments[0].paymentMethod', path: 'advancePayments[0].paymentMethod' },
        { label: 'Card Type', value: 'advancePayments[0].cardType', path: 'advancePayments[0].cardType' },
        { label: 'Card Holder Name', value: 'advancePayments[0].cardHolderName', path: 'advancePayments[0].cardHolderName' },
        { label: 'Payment Status', value: 'advancePayments[0].status', path: 'advancePayments[0].status' },
        { label: 'Payment Currency', value: 'advancePayments[0].currency', path: 'advancePayments[0].currency' },
        { label: 'Created By', value: 'advancePayments[0].createdBy', path: 'advancePayments[0].createdBy' }
      ]
    },
    {
      title: 'Yard Information',
      icon: <EnvironmentOutlined style={{ color: '#a0d911' }} />,
      key: 'yardInfo',
      color: '#a0d911',
      fields: [
        { label: 'Yard ID', value: 'yard[0].yardId', path: 'yard[0].yardId' },
        { label: 'Yard Price', value: 'yard[0].price', path: 'yard[0].price' },
        { label: 'Yard Description', value: 'yard[0].description', path: 'yard[0].description' }
      ]
    }
  ];

  useEffect(() => {
    // Update localDateRange when dateRange prop changes (when user changes date in main table)
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      setLocalDateRange(dateRange);
    }
  }, [dateRange]);

  // Flatten all fields for backward compatibility
  const allPossibleFields = fieldGroups.flatMap(group =>
    group.fields.map(field => field.value)
  );

  const [modalVisible, setModalVisible] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [selectedFields, setSelectedFields] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [includeAllColumns, setIncludeAllColumns] = useState(true);
  const [expandedPanels, setExpandedPanels] = useState(fieldGroups.map(group => group.key));
  const [estimatedData, setEstimatedData] = useState(null);
  const [isCustomDateRange, setIsCustomDateRange] = useState(false);

  // Helper function to get nested value from object (updated for arrays)
  const getNestedValue = (obj, path) => {
    if (!obj || !path) return '';

    try {
      // Not stored on the lead: read from the landing URL's query string.
      if (path === 'gclid') {
        return extractGclid(obj.landingUrl);
      }

      if (path === 'phoneValidation.reason') {
        const phoneValidation = obj.phoneValidation;
        if (!phoneValidation || Object.keys(phoneValidation).length === 0) {
          return 'N/A';
        }

        const reason = phoneValidation.reason;
        return reason && String(reason).trim() ? reason : 'Valid Format';
      }

      if (path.startsWith('yard[0].')) {
        const currentYard = getCurrentYardEntry(obj.yard);
        return getNestedValue(currentYard, path.slice('yard[0].'.length));
      }

      // quotedValue is the full re-quote history, oldest first; the Quoted Value
      // columns report the current (newest) quote, not the original one.
      if (path.startsWith('quotedValue[0].')) {
        const quotes = Array.isArray(obj.quotedValue) ? obj.quotedValue : [];
        return getNestedValue(quotes[quotes.length - 1], path.slice('quotedValue[0].'.length));
      }

      const value = path.split('.').reduce((acc, key) => {
        if (acc === null || acc === undefined) return '';

        // Handle array indices like quotes[0]
        const arrayMatch = key.match(/(\w+)\[(\d+)\]/);
        if (arrayMatch) {
          const [, arrKey, index] = arrayMatch;
          return acc[arrKey]?.[parseInt(index)] ?? '';
        }

        return acc[key] ?? '';
      }, obj);

      // Format phone numbers
      if (path.includes('phoneNumber') && value) {
        return formatPhoneNumber(value);
      }

      // Format dates
      if ((path.includes('Date') || path.includes('At') || path.includes('createdAt') ||
        path.includes('updatedAt') || path.includes('queuedAt') ||
        path.includes('assignedAt') || path.includes('changedAt') ||
        path.includes('addedAt')) && value) {
        return formatDate(value);
      }

      // Handle boolean values
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }

      // Handle array values
      if (Array.isArray(value)) {
        if (value.length === 0) return 'None';

        // For simple arrays (like languages)
        if (typeof value[0] !== 'object') {
          return value.join(', ');
        }

        // For object arrays, show count
        return `${value.length} item(s)`;
      }

      // Handle object values
      if (typeof value === 'object' && value !== null) {
        return Object.keys(value).length > 0 ? 'Object' : '';
      }

      return value;
    } catch (error) {
      console.warn(`Error getting value for path ${path}:`, error);
      return '';
    }
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    const digits = phoneNumber.replace(/\D/g, '');
    return digits ? `+${digits}` : '';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return dayjs(dateString).format('MM/DD/YYYY HH:mm:ss');
  };


  const fetchLeadsByDateRange = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("userData"));

      if (!userData?.id) {
        message.error("Authentication issue — please login again");
        return [];
      }

      let params = {
        page: 1,
        limit: 1000000, // Get all records for export
      };

      const isSearching = false; // Export doesn't use search

      // Date range logic - use localDateRange from modal
      if (localDateRange && localDateRange.length === 2) {
        const [startDate, endDate] = localDateRange;

        // Check if we have a stage filter that's not "New"
        if (filters?.status && filters.status !== "New") {
          // For non-New stages, use stage history filtering
          params.stageHistoryStage = filters.status;
          params.stageHistoryDate = startDate.startOf('day').format('YYYY-MM-DD HH:mm:ss');
          params.stageHistoryEndDate = endDate.endOf('day').format('YYYY-MM-DD HH:mm:ss');
        } else {
          // Default: use createdAt
          params.dateField = 'createdAt';
          params.startDate = startDate.startOf('day').format('YYYY-MM-DD HH:mm:ss');
          params.endDate = endDate.endOf('day').format('YYYY-MM-DD HH:mm:ss');
        }
      }

      // Apply all filters from the main component
      if (filters?.status) {
        params.status = filters.status;
      }

      if (filters?.type) {
        params.type = filters.type;
      }

      if (filters?.leadStatus) {
        params.leadStatus = filters.leadStatus;
      }

      if (filters?.source) {
        params.source = filters.source;
      }

      if (filters?.tierType) {
        params.tierType = filters.tierType;
      }

      // Agent filtering logic
      const loginAgent = JSON.parse(localStorage.getItem("userData") || "{}");

      if (loginAgent?.role === "SALES-AGENT") {
        params.agentId = userData.id;
        params.agentMode = "include";
      } else if (loginAgent?.role === "ADMIN" && filters?.agentId) {
        params.agentId = filters.agentId;
      }

      console.log('Exporting leads with params:', params);

      // The server sends only the fields this page uses
      // (LeadController LEAD_VIEW_FIELDS['lead-export']).
      params.view = 'lead-export';
      const response = await API.leadGetAll(params);

      // Extract data correctly
      let leadsData = [];
      if (response && (response.status === true || response.success === true || response.statusCode === 200)) {
        leadsData = response.data || response.leads || [];
      } else if (Array.isArray(response)) {
        leadsData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        leadsData = response.data;
      }

      // Also check pagination total for accurate count
      const total = response?.pagination?.totalRecords || leadsData.length;
      console.log(`Export fetched ${leadsData.length} leads (Total available: ${total})`);

      // Estimate file size
      const estimatedSize = estimateFileSize(leadsData, selectedFields.length || allPossibleFields.length);
      setEstimatedData({
        count: leadsData.length,
        fileSize: estimatedSize,
        fieldsCount: selectedFields.length || allPossibleFields.length,
        dateRange: localDateRange
      });

      return leadsData;
    } catch (err) {
      console.error('Error fetching leads:', err);
      message.error('Failed to fetch leads for export');
      return [];
    }
  };

  const estimateFileSize = (data, fieldCount) => {
    // Rough estimation: each field ~50 bytes, each row ~ fieldCount * 50 bytes
    const estimatedBytes = data.length * fieldCount * 50;
    if (estimatedBytes < 1024) return `${estimatedBytes} B`;
    if (estimatedBytes < 1024 * 1024) return `${(estimatedBytes / 1024).toFixed(1)} KB`;
    return `${(estimatedBytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const removeDuplicates = (data) => {
    const uniqueLeads = [];
    const phoneSet = new Set();
    const emailSet = new Set();

    data.forEach((row) => {
      const phone = getNestedValue(row, 'personalInfo.phoneNumber');
      const email = getNestedValue(row, 'personalInfo.email');

      if (!phoneSet.has(phone) && !emailSet.has(email)) {
        phoneSet.add(phone);
        emailSet.add(email);
        uniqueLeads.push(row);
      }
    });

    return uniqueLeads;
  };

  const validLeadStages = [
    "New", "Call Back", "Voice Mail", "Follow-Up", "Quoted",
    "Pending Payment", "Payment Completed", "Sales Made",
    "Part Not Available", "Wrong Number", "Not Interested",
    "Hang Up", "Fake", "Test Lead", "Negotiation", "Error"
  ];

  const handleDownloadExcel = async (useCustom = false) => {
    setDownloading(true);

    try {
      const allData = await fetchLeadsByDateRange();

      if (allData.length === 0) {
        message.warning('No leads found for the selected date range');
        return;
      }

      const validLeads = allData.filter((lead) =>
        validLeadStages.includes(lead.leadStage)
      );

      if (validLeads.length === 0) {
        message.warning('No valid leads found for export');
        return;
      }

      // REMOVE THIS LINE - Don't deduplicate
      // const uniqueLeads = removeDuplicates(validLeads);

      // Use validLeads directly instead of uniqueLeads
      let fieldsToInclude = [];
      if (useCustom) {
        fieldsToInclude = selectedFields;
      } else if (includeAllColumns) {
        fieldsToInclude = allPossibleFields;
      } else {
        fieldsToInclude = [
          'agentName',
          'vehicleInfo.year',
          'vehicleInfo.make',
          'vehicleInfo.model',
          'vehicleInfo.part',
          'personalInfo.fullName',
          'personalInfo.email',
          'personalInfo.phoneNumber',
          'leadStage',
          'createdAt',
          'locationInfo.timezone.id',
          'locationInfo.timezone.abbr'
        ];
      }

      // Prepare worksheet data with proper column names - use validLeads instead of uniqueLeads
      const worksheetData = validLeads.map((row) => {
        const rowData = {};

        fieldsToInclude.forEach((fieldPath) => {
          let value = getNestedValue(row, fieldPath);

          // Find the field in groups to get proper label
          let headerName = '';
          for (const group of fieldGroups) {
            const field = group.fields.find(f => f.value === fieldPath);
            if (field) {
              headerName = field.label;
              break;
            }
          }

          // Fallback if not found in groups
          if (!headerName) {
            headerName = fieldPath
              .split('.')
              .map(part => part.charAt(0).toUpperCase() + part.slice(1))
              .join(' ');
          }

          rowData[headerName] = value;
        });

        return rowData;
      });

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();

      // Auto-size columns
      const maxWidths = {};
      worksheetData.forEach(row => {
        Object.keys(row).forEach(key => {
          const cellValue = row[key]?.toString() || '';
          const length = cellValue.length;
          if (!maxWidths[key] || length > maxWidths[key]) {
            maxWidths[key] = Math.min(length, 50);
          }
        });
      });

      worksheet['!cols'] = Object.keys(maxWidths).map(key => ({
        wch: maxWidths[key] + 2
      }));

      // Generate filename with selected date range
      const now = new Date();
      const pad = (n) => n.toString().padStart(2, '0');
      const formattedDate = `${pad(now.getMonth() + 1)}${pad(now.getDate())}${now.getFullYear()}`;

      let dateRangeText = '';
      if (localDateRange && localDateRange.length === 2) {
        const [start, end] = localDateRange;
        dateRangeText = `_${start.format('MMDDYY')}-${end.format('MMDDYY')}`;
      }

      const sheetName = 'Leads_Export';
      const fileName = `Leads_Export${dateRangeText}_${formattedDate}.xlsx`;

      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      XLSX.writeFile(workbook, fileName);

      // Update success message to show validLeads length
      message.success(`Exported ${validLeads.length} leads from ${localDateRange[0].format('MMM D')} to ${localDateRange[1].format('MM-DD-YYYY')}`);
      setModalVisible(false);
      setCustomMode(false);
      setSelectedFields([]);

    } catch (err) {
      console.error('Error exporting leads:', err);
      message.error('Failed to export leads');
    } finally {
      setDownloading(false);
    }
  };

  const openModal = async () => {
    if (includeAllColumns) {
      setSelectedFields(allPossibleFields);
    }

    // Reset to main table date range when opening modal
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      setLocalDateRange(dateRange);
      setIsCustomDateRange(false);
    }

    // Fetch initial data for estimation
    const data = await fetchLeadsByDateRange();
    const estimatedSize = estimateFileSize(data, allPossibleFields.length);
    setEstimatedData({
      count: data.length,
      fileSize: estimatedSize,
      fieldsCount: allPossibleFields.length,
      dateRange: localDateRange
    });

    setModalVisible(true);
  };

  const handleSelectAll = () => {
    if (selectedFields.length === allPossibleFields.length) {
      setSelectedFields([]);
    } else {
      setSelectedFields(allPossibleFields);
    }
  };

  const handleSelectGroup = (groupId, selectAll) => {
    const group = fieldGroups.find(g => g.key === groupId);
    if (!group) return;

    const groupFields = group.fields.map(f => f.value);

    setSelectedFields(prev => {
      const newFields = [...prev];

      if (selectAll) {
        // Add fields not already selected
        groupFields.forEach(field => {
          if (!newFields.includes(field)) {
            newFields.push(field);
          }
        });
      } else {
        // Remove all fields from this group
        return newFields.filter(field => !groupFields.includes(field));
      }

      return newFields;
    });
  };

  const isGroupAllSelected = (groupId) => {
    const group = fieldGroups.find(g => g.key === groupId);
    if (!group) return false;

    const groupFields = group.fields.map(f => f.value);
    return groupFields.every(field => selectedFields.includes(field));
  };

  const isGroupSomeSelected = (groupId) => {
    const group = fieldGroups.find(g => g.key === groupId);
    if (!group) return false;

    const groupFields = group.fields.map(f => f.value);
    return groupFields.some(field => selectedFields.includes(field)) &&
      !groupFields.every(field => selectedFields.includes(field));
  };

  const getSelectionPercentage = () => {
    return Math.round((selectedFields.length / allPossibleFields.length) * 100);
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setLocalDateRange(dates);
      setIsCustomDateRange(true);
      // Clear estimated data when date range changes
      setEstimatedData(null);
    } else {
      // Reset to main table date range if cleared
      if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
        setLocalDateRange(dateRange);
        setIsCustomDateRange(false);
      } else {
        // Reset to today if no main table date range
        setLocalDateRange([dayjs().startOf('day'), dayjs().endOf('day')]);
        setIsCustomDateRange(false);
      }
    }
  };

  return (
    <div className="download-buttons">
      <Tooltip title="Export Leads to Excel">
        <Button
          onClick={openModal}
          disabled={downloading}
          {...buttonProps}
          style={{
            ...buttonProps?.style,
            border: '1px solid #d9d9d9',
            height: 40,
            fontWeight: 600,
            padding: '10px',
            borderRadius: 8
          }}
        >
          <Space>
            <FileExcelOutlined />
            {buttonProps?.children || 'Export Leads'}

          </Space>
        </Button>
      </Tooltip>

      <Modal
        title={
          <Space align="center">
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              width: 40,
              height: 40,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: 12
            }}>
              <FileExcelOutlined style={{ color: 'white', fontSize: 20 }} />
            </div>
            <div>
              <Title level={4} style={{ margin: 0 }}>Export Leads</Title>
              <Text type="secondary">Generate Excel report with lead data</Text>
            </div>
          </Space>
        }
        open={modalVisible}
        onCancel={() => {
          if (!downloading) {
            setModalVisible(false);
            setCustomMode(false);
            setSelectedFields([]);
            setIsCustomDateRange(false);
          }
        }}
        footer={null}
        width={900}
        maskClosable={!downloading}
        style={{ top: 20 }}
        className="lead-export-modal"
      >


        <Card
          style={{
            marginBottom: 20,
            borderRadius: 8,
            background: '#fafafa'
          }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>
                <CalendarOutlined /> Select Date Range for Export
              </Text>
              {dateRange && dateRange.length === 2 && !isCustomDateRange && (
                <Tooltip title="Click to use table date range">
                  <Tag color="blue" style={{ cursor: 'pointer' }} onClick={() => {
                    setLocalDateRange(dateRange);
                    setIsCustomDateRange(false);
                    setEstimatedData(null);
                  }}>
                    <CalendarOutlined /> Use Table Range
                  </Tag>
                </Tooltip>
              )}
            </div>

            <RangePicker
              value={localDateRange}
              onChange={handleDateRangeChange}
              style={{ width: '100%' }}
              allowClear
              format="MMM DD, YYYY"
              presets={[
                { label: 'Today', value: [dayjs().startOf('day'), dayjs().endOf('day')] },
                { label: 'Yesterday', value: [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')] },
                { label: 'Last 7 Days', value: [dayjs().subtract(7, 'day').startOf('day'), dayjs().endOf('day')] },
                { label: 'Last 30 Days', value: [dayjs().subtract(30, 'day').startOf('day'), dayjs().endOf('day')] },
              ]}
            />

            {localDateRange?.length === 2 && (
              <div>
                <Text type="secondary">
                  Exporting leads from{" "}
                  <strong>{localDateRange[0].format('MM-DD-YYYY')}</strong> →{" "}
                  <strong>{localDateRange[1].format('MM-DD-YYYY')}</strong>
                </Text>

              </div>
            )}
          </Space>
        </Card>

        {!customMode ? (
          <>
            <div style={{ marginBottom: 32 }}>

              <Divider />

              <div className="button-container" style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                <Button
                  type="primary"
                  onClick={() => handleDownloadExcel(false)}
                  disabled={downloading || !localDateRange || localDateRange.length !== 2}
                  loading={downloading}
                  icon={<FileExcelOutlined />}
                  size="large"
                  style={{
                    flex: 1,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                    height: 48,
                    fontSize: 16,
                    fontWeight: 600
                  }}
                >
                  {includeAllColumns ? 'Download Complete Report' : 'Download Essential Report'}
                </Button>

                <Button
                  type="default"
                  onClick={() => setCustomMode(true)}
                  disabled={downloading || !localDateRange || localDateRange.length !== 2}
                  size="large"
                  style={{
                    flex: 1,
                    height: 48,
                    fontSize: 16,
                    borderColor: '#1890ff',
                    color: '#1890ff'
                  }}
                >
                  Customize Report Columns
                </Button>

                <GoogleAdsSheetDownload
                  dateRange={localDateRange}
                  disabled={downloading}
                />
              </div>
            </div>
          </>
        ) : (
          <>


            <div style={{ marginBottom: 16 }}>
              <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'end' }}>
                <Space size="middle">
                  <div style={{ minWidth: 120 }}>
                    <Progress
                      percent={getSelectionPercentage()}
                      size="small"
                      strokeColor="#52c41a"
                      showInfo={false}
                    />
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {selectedFields.length}/{allPossibleFields.length} selected
                  </Text>
                  <Button
                    size="middle"
                    onClick={handleSelectAll}
                    type={selectedFields.length === allPossibleFields.length ? 'primary' : 'default'}
                    icon={selectedFields.length === allPossibleFields.length ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
                  >
                    {selectedFields.length === allPossibleFields.length ? 'Deselect All' : 'Select All'}
                  </Button>

                </Space>


              </Space>

              {/* Main content without heavy scrolling */}
              <div style={{
                borderRadius: 12,
                marginBottom: 24,
                border: '1px solid #e8e8e8',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                background: '#ffffff',
                overflow: 'hidden'
              }}>
                <style>
                  {`
      .custom-scrollbar::-webkit-scrollbar {
        width: 10px;
      }
      
      .custom-scrollbar::-webkit-scrollbar-track {
        background: #f5f5f5;
        border-radius: 10px;
      }
      
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: #d0d0d0;
        border-radius: 10px;
        border: 2px solid #f5f5f5;
      }
      
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: #b0b0b0;
      }
      
      .custom-scrollbar::-webkit-scrollbar-thumb:active {
        background: #999999;
      }
      
      /* For Firefox */
      .custom-scrollbar {
        scrollbar-width: thin;
        scrollbar-color: #d0d0d0 #f5f5f5;
      }

      /* Custom scrollbar for tabs sidebar */
      .ant-tabs-nav-list {
        overflow-y: scroll !important;
        max-height: 468px;
        padding-right: 8px;
      }

      .ant-tabs-nav-list::-webkit-scrollbar {
        width: 8px;
      }
      
      .ant-tabs-nav-list::-webkit-scrollbar-track {
        background: #f0f0f0;
        border-radius: 8px;
      }
      
      .ant-tabs-nav-list::-webkit-scrollbar-thumb {
        background: #c0c0c0;
        border-radius: 8px;
        border: 2px solid #f0f0f0;
      }
      
      .ant-tabs-nav-list::-webkit-scrollbar-thumb:hover {
        background: #a0a0a0;
      }
      
      .ant-tabs-nav-list::-webkit-scrollbar-thumb:active {
        background: #888888;
      }
    `}
                </style>

                <Tabs
                  tabPosition="left"
                  type="card"
                  activeKey={activeTab}
                  onChange={setActiveTab}
                  style={{ height: 500 }}
                  tabBarStyle={{
                    width: 320,
                    paddingTop: 16,
                    background: '#fafbfc',
                    borderRight: '1px solid #f0f0f0',
                    overflow: 'hidden'
                  }}
                >
                  {fieldGroups.map((group) => (
                    <Tabs.TabPane
                      key={group.key}
                      tab={
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: '12px 16px',
                          width: '100%',
                          borderRadius: 8,
                          transition: 'all 0.3s ease',
                          marginBottom: 8,
                          background: activeTab === group.key ? '#ffffff' : 'transparent',
                          boxShadow: activeTab === group.key ? '0 1px 4px rgba(0,0,0,0.06)' : 'none',
                          border: activeTab === group.key ? `1px solid ${group.color}40` : '1px solid transparent'
                        }}>
                          <div style={{
                            background: `${group.color}12`,
                            padding: 10,
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 40,
                            minHeight: 40,
                            fontSize: 18
                          }}>
                            {group.icon}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Text strong style={{
                              fontSize: 14,
                              display: 'block',
                              marginBottom: 4,
                              color: activeTab === group.key ? group.color : '#595959'
                            }}>
                              {group.title}
                            </Text>
                            <div style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              background: `${group.color}10`,
                              padding: '2px 8px',
                              borderRadius: 12,
                              border: `1px solid ${group.color}20`
                            }}>
                              <Text style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: group.color
                              }}>
                                {group.fields.filter(f => selectedFields.includes(f.value)).length}/{group.fields.length}
                              </Text>
                            </div>
                          </div>
                          <Checkbox
                            checked={isGroupAllSelected(group.key)}
                            indeterminate={isGroupSomeSelected(group.key)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleSelectGroup(group.key, e.target.checked);
                            }}
                            disabled={downloading}
                            style={{
                              transform: 'scale(1.15)'
                            }}
                          />
                        </div>
                      }
                    >
                      <div
                        className="custom-scrollbar"
                        style={{
                          height: 460,
                          overflowY: 'scroll',
                          padding: 20,
                          paddingRight: 12,
                          background: '#fafbfc',
                          borderRadius: '0 12px 12px 0'
                        }}
                      >
                        <Row gutter={[16, 16]}>
                          {group.fields.map((field) => {
                            const isSelected = selectedFields.includes(field.value);

                            return (
                              <Col span={12} key={field.value}>
                                <Card
                                  size="small"
                                  hoverable
                                  onClick={() => {
                                    if (selectedFields.includes(field.value)) {
                                      setSelectedFields(selectedFields.filter(f => f !== field.value));
                                    } else {
                                      setSelectedFields([...selectedFields, field.value]);
                                    }
                                  }}
                                  style={{
                                    borderColor: isSelected ? `${group.color}60` : '#e8e8e8',
                                    borderWidth: 1.5,
                                    background: isSelected
                                      ? `${group.color}06`
                                      : '#ffffff',
                                    cursor: 'pointer',
                                    height: '100%',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    transform: isSelected ? 'translateY(-2px)' : 'translateY(0)',
                                    boxShadow: isSelected
                                      ? `0 2px 8px ${group.color}15`
                                      : '0 1px 2px rgba(0,0,0,0.04)'
                                  }}
                                  bodyStyle={{ padding: '14px' }}
                                >
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 10
                                  }}>
                                    <Checkbox
                                      checked={selectedFields.includes(field.value)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedFields([...selectedFields, field.value]);
                                        } else {
                                          setSelectedFields(selectedFields.filter(f => f !== field.value));
                                        }
                                      }}
                                      disabled={downloading}
                                      style={{
                                        transform: 'scale(1.1)',
                                        marginTop: 2
                                      }}
                                    />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <Tooltip title={field.path}>
                                        <Text
                                          strong
                                          style={{
                                            fontSize: 13.5,
                                            cursor: 'help',
                                            display: 'block',
                                            marginBottom: 4,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            color: isSelected ? group.color : '#595959',
                                            fontWeight: isSelected ? 600 : 500
                                          }}
                                        >
                                          {field.label}
                                        </Text>
                                      </Tooltip>
                                    </div>
                                    {selectedFields.includes(field.value) && (
                                      <CheckCircleOutlined
                                        style={{
                                          color: group.color,
                                          fontSize: 16,
                                          marginTop: 2
                                        }}
                                      />
                                    )}
                                  </div>
                                </Card>
                              </Col>
                            );
                          })}
                        </Row>
                      </div>
                    </Tabs.TabPane>
                  ))}
                </Tabs>
              </div>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: 16,
              borderTop: '1px solid #f0f0f0'
            }}>
              <Button
                onClick={() => {
                  setCustomMode(false);
                  setSelectedFields([]);
                }}
                disabled={downloading}
                icon={<ArrowLeftOutlined />}
                size="large"
              >
                Back to Options
              </Button>

              <Space>
                <Button
                  type="default"
                  onClick={() => setSelectedFields([])}
                  disabled={selectedFields.length === 0 || downloading}
                  size="large"
                >
                  Clear Selection
                </Button>
                <Button
                  type="primary"
                  onClick={() => handleDownloadExcel(true)}
                  disabled={selectedFields.length === 0 || downloading || !localDateRange || localDateRange.length !== 2}
                  loading={downloading}
                  icon={<FileExcelOutlined />}
                  size="large"
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                    padding: '0 32px',
                    height: 48,
                    fontSize: 16,
                    fontWeight: 600
                  }}
                >
                  Export Selected ({selectedFields.length} Fields)
                </Button>
              </Space>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default LeadDownload;


